import type { Catalogue } from '@daghfal/core';

/**
 * The guardian app's messages.
 *
 * This is the one surface read by people with no training and no manual (NFR-USE-06), so the register is
 * the plainest of the four: no system vocabulary, no role names, nothing a parent would have to learn.
 */
export const strings: Catalogue = {
  ar: {
    'app.name': 'دغفل',
    'app.subtitle': 'تابع يوم طفلك',

    'nav.home': 'الرئيسية',
    'nav.day': 'يومي اليوم',
    'nav.photos': 'الصور',
    'nav.messages': 'الرسائل',
    'nav.payments': 'المدفوعات',
    'nav.attendance': 'الحضور',
    'nav.complaints': 'الشكاوى',
    'nav.info': 'معلومات الطفل',

    'children.choose': 'اختر الطفل',
    'children.none': 'لا يوجد أطفال مرتبطون بحسابك. تواصل مع إدارة الحضانة.',
    'children.switch': 'تبديل الطفل',
    'children.paymentsOnly': 'غادر الحضانة · المدفوعات فقط',
    'payments.afterLeaving': 'غادر طفلك الحضانة. تبقى صفحة المدفوعات متاحة لك حتى يُسدَّد الرصيد المستحق، ثم تُغلق.',
    'children.inNursery': 'في {nursery}',

    'home.today': 'اليوم',
    'home.day': 'يومي اليوم',
    'home.day.hint': 'الطعام والنوم والنشاط',
    'home.photos': 'الصور',
    'home.photos.hint': 'صور اليوم والمناسبات',
    'home.messages': 'الرسائل',
    'home.messages.hint': 'التواصل مع المعلّمة',
    'home.payments': 'المدفوعات',
    'home.payments.hint': 'الرسوم والإيصالات',
    'home.info': 'معلومات الطفل',
    'home.info.hint': 'الصحة وجهات الاتصال',
    'home.complaints': 'الشكاوى',
    'home.complaints.hint': 'ملاحظة أو أمر يزعجك',
    'home.incidents': 'الحوادث',
    'home.incidents.hint': 'ما سجّلته الحضانة، للاطّلاع عليه',
    'home.calendar': 'التقويم',
    'home.calendar.hint': 'العطل والإغلاقات والرحلات القادمة',
    'home.attendance': 'الحضور والغياب',
    'home.attendance.hint': 'أيام الحضور، وإبلاغ الحضانة بغياب قادم',

    'notices.title': 'بلاغات الغياب المرسلة',
    'notices.none': 'لا بلاغات غياب قادمة.',
    'notices.withdraw': 'سحب البلاغ',
    'notices.withdrawOf': 'سحب بلاغ {days}',
    'notices.withdrawHint': 'ستنتظر الحضانة الطفل في هذه الأيام. إن لم يحضر يُعدّ غائبًا دون إبلاغ.',
    'notices.withdrawConfirm': 'تأكيد السحب',
    'notices.started': 'بدأ الغياب، فلا يُسحب البلاغ. إن عاد الطفل باكرًا فأحضره كالمعتاد.',

    'info.pickups': 'من يحقّ له استلام الطفل',
    'info.pickups.hint': 'إضافةً إلى أولياء الأمر. تسجّلهم إدارة الحضانة.',
    'info.pickups.none': 'لم يُسجَّل أحد غير أولياء الأمر.',
    'info.pickups.from': 'من {date}',
    'info.pickups.until': 'حتى {date}',
    'info.pickups.expired': 'انتهت المدة',
    'info.medications': 'الأدوية',
    'info.medications.hint': 'ما سجّلته الحضانة من أدوية الطفل، وما أُعطي منها اليوم.',
    'info.medications.none': 'لا أدوية مسجّلة.',
    'info.medication.givenToday.zero': 'لم يُعطَ اليوم',
    'info.medication.givenToday.one': 'أُعطي مرة واحدة اليوم',
    'info.medication.givenToday.two': 'أُعطي مرتين اليوم',
    'info.medication.givenToday.few': 'أُعطي {count} مرات اليوم',
    'info.medication.givenToday.many': 'أُعطي {count} مرة اليوم',
    'info.medication.givenToday.other': 'أُعطي {count} مرة اليوم',

    'change.title': 'طلب تعديل',
    'change.hint': 'تصل رسالتك إلى إدارة الحضانة، وهي التي تُجري التعديل ثم تردّ عليك في الرسائل.',
    'change.part': 'ما الذي تريد تعديله؟',
    'change.part.contacts': 'جهات الاتصال في الطوارئ',
    'change.part.health': 'الحساسيات والصحة',
    'change.part.pickups': 'من يستلم الطفل',
    'change.part.medication': 'الأدوية',
    'change.part.other': 'غير ذلك',
    'change.to': 'إلى',
    'change.body': 'ما التعديل المطلوب؟',
    'change.body.hint': 'اكتب ما يجب أن تصبح عليه البيانات، مثل رقم الهاتف الجديد.',
    'change.subject': 'طلب تعديل: {part} – {name}',
    'change.send': 'إرسال الطلب',

    'info.title': 'معلومات الطفل',
    'info.class': 'الصف',
    'info.dob': 'تاريخ الميلاد',
    'info.age': 'العمر',
    'info.enrolled': 'تاريخ التسجيل',
    'info.allergies': 'الحساسيات',
    'info.allergies.none': 'لا توجد حساسيات مسجّلة.',
    'info.contacts': 'جهات الاتصال في الطوارئ',
    'info.contacts.none': 'لم تُسجَّل جهات اتصال بعد.',
    'info.contacts.hint': 'لتعديل هذه البيانات تواصل مع إدارة الحضانة.',
    'info.severity.Mild': 'خفيفة',
    'info.severity.Severe': 'شديدة',
    'info.severity.Anaphylactic': 'مفرطة (تهدّد الحياة)',

    'day.title': 'يومي اليوم',
    'photos.title': 'الصور',
    'messages.title': 'الرسائل',
    'payments.title': 'المدفوعات',

    // Complaints (§4.14). The plainest register in the product: this screen is read by someone who is
    // upset, and every extra word is a word between them and being heard.
    'complaints.title': 'الشكاوى والملاحظات',
    'complaints.raise': 'تقديم شكوى',
    'complaints.send': 'إرسال',
    'complaints.sent': 'وصلت شكواك إلى إدارة الحضانة. سنخبرك بكل تطوّر عليها.',
    'complaints.subject': 'عنوان الشكوى',
    'complaints.subject.hint': 'سطر واحد يلخّص الموضوع.',
    'complaints.body': 'ما الذي حدث؟',
    'complaints.body.hint': 'اكتب ما حدث ومتى. لا تقلق بشأن الصياغة.',
    'complaints.about': 'هل تخصّ الشكوى موظّفاً بعينه؟',
    'complaints.about.hint': 'اختياري. لن يطّلع هذا الموظّف على شكواك ولن يعلم بوجودها، ولن تُسنَد إليه متابعتها.',
    'complaints.about.none': 'لا تخصّ شخصاً بعينه',
    'complaints.privacy': 'لا يطّلع على الشكوى إلا إدارة الحضانة. والموظّف الذي تخصّه — إن ذكرته — لا يراها ولا يعلم بها.',
    'complaints.none': 'لا توجد شكاوى',
    'complaints.none.hint': 'إن كان لديك ملاحظة أو أمر يزعجك، اكتبه هنا وستصل الإدارة مباشرةً.',
    'complaints.timeline': 'ما جرى على الشكوى',
    'complaints.reply': 'إضافة ردّ',
    'complaints.reopens': 'إرسال ردّ الآن يعيد فتح الشكوى لدى الإدارة.',
    'complaints.withdraw': 'سحب الشكوى',
    'complaints.awaiting': 'وصلت شكواك وهي بانتظار اطّلاع الإدارة.',
  },

  en: {
    'app.name': 'Daghfal',
    'app.subtitle': 'Follow your child’s day',

    'nav.home': 'Home',
    'nav.day': 'My day',
    'nav.photos': 'Photos',
    'nav.messages': 'Messages',
    'nav.payments': 'Payments',
    'nav.attendance': 'Attendance',
    'nav.complaints': 'Complaints',
    'nav.info': 'Child info',

    'children.choose': 'Choose a child',
    'children.none': 'No children are linked to your account. Please contact the nursery.',
    'children.switch': 'Switch child',
    'children.paymentsOnly': 'Left the nursery · payments only',
    'payments.afterLeaving': 'Your child has left the nursery. The payments screen stays open to you until the balance due is paid, then closes.',
    'children.inNursery': 'at {nursery}',

    'home.today': 'Today',
    'home.day': 'My day',
    'home.day.hint': 'Meals, sleep and activities',
    'home.photos': 'Photos',
    'home.photos.hint': 'Today and occasions',
    'home.messages': 'Messages',
    'home.messages.hint': 'Talk to the teacher',
    'home.payments': 'Payments',
    'home.payments.hint': 'Fees and receipts',
    'home.info': 'Child info',
    'home.info.hint': 'Health and contacts',
    'home.complaints': 'Complaints',
    'home.complaints.hint': 'Something worrying you',
    'home.incidents': 'Incidents',
    'home.incidents.hint': 'What the nursery recorded, to read',
    'home.calendar': 'Calendar',
    'home.calendar.hint': 'Holidays, closures and trips coming up',
    'home.attendance': 'Attendance and absence',
    'home.attendance.hint': 'Days attended, and telling the nursery about an absence',

    'notices.title': 'Absence notices sent',
    'notices.none': 'No absence notices coming up.',
    'notices.withdraw': 'Withdraw',
    'notices.withdrawOf': 'Withdraw the notice for {days}',
    'notices.withdrawHint': 'The nursery will expect your child on these days. If they do not come, it counts as an unexplained absence.',
    'notices.withdrawConfirm': 'Confirm withdrawing',
    'notices.started': 'The absence has started, so the notice cannot be withdrawn. If your child is back early, just bring them in.',

    'info.pickups': 'Who may collect your child',
    'info.pickups.hint': 'Besides the guardians. The nursery records them.',
    'info.pickups.none': 'No one is recorded besides the guardians.',
    'info.pickups.from': 'From {date}',
    'info.pickups.until': 'Until {date}',
    'info.pickups.expired': 'Expired',
    'info.medications': 'Medication',
    'info.medications.hint': "The medication the nursery recorded for your child, and what was given today.",
    'info.medications.none': 'No medication recorded.',
    'info.medication.givenToday.zero': 'Not given today',
    'info.medication.givenToday.one': 'Given once today',
    'info.medication.givenToday.other': 'Given {count} times today',

    'change.title': 'Ask for a change',
    'change.hint': 'Your message goes to the nursery\'s administration. They make the change and reply in Messages.',
    'change.part': 'What do you want to change?',
    'change.part.contacts': 'Emergency contacts',
    'change.part.health': 'Allergies and health',
    'change.part.pickups': 'Who collects your child',
    'change.part.medication': 'Medication',
    'change.part.other': 'Something else',
    'change.to': 'To',
    'change.body': 'What should change?',
    'change.body.hint': 'Write what the details should become, like the new phone number.',
    'change.subject': 'Change request: {part} – {name}',
    'change.send': 'Send the request',

    'info.title': 'Child information',
    'info.class': 'Class',
    'info.dob': 'Date of birth',
    'info.age': 'Age',
    'info.enrolled': 'Enrolled',
    'info.allergies': 'Allergies',
    'info.allergies.none': 'No allergies recorded.',
    'info.contacts': 'Emergency contacts',
    'info.contacts.none': 'No emergency contacts recorded yet.',
    'info.contacts.hint': 'To change these details, contact the nursery.',
    'info.severity.Mild': 'Mild',
    'info.severity.Severe': 'Severe',
    'info.severity.Anaphylactic': 'Anaphylactic',

    'day.title': 'My day',
    'photos.title': 'Photos',
    'messages.title': 'Messages',
    'payments.title': 'Payments',

    'complaints.title': 'Complaints and concerns',
    'complaints.raise': 'Raise a concern',
    'complaints.send': 'Send',
    'complaints.sent': 'Your complaint has reached the nursery. We will tell you about every step.',
    'complaints.subject': 'What is it about?',
    'complaints.subject.hint': 'One line summing it up.',
    'complaints.body': 'What happened?',
    'complaints.body.hint': 'Write what happened and when. Do not worry about the wording.',
    'complaints.about': 'Is this about a particular member of staff?',
    'complaints.about.hint': 'Optional. They will not see your complaint, will not know it exists, and will not be given it to deal with.',
    'complaints.about.none': 'Not about a particular person',
    'complaints.privacy': 'Only the nursery management sees this. The member of staff it names, if you name one, does not see it and is not told about it.',
    'complaints.none': 'No complaints',
    'complaints.none.hint': 'If something is worrying you, write it here and it goes straight to the management.',
    'complaints.timeline': 'What has happened',
    'complaints.reply': 'Add a reply',
    'complaints.reopens': 'Sending a reply now reopens this complaint with the management.',
    'complaints.withdraw': 'Withdraw this complaint',
    'complaints.awaiting': 'Your complaint has arrived and is waiting to be read.',
  },
};
